








































































































































































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { UserModule } from "@/store/modules/UserModule";
import { firestore } from "firebase/app";
import { formatDateShort, formatTime } from "@/utils/DateFormatter";

import { deleteAppointment, updateSyncTargets } from "@/data/repository/ProjectStatusRepository";
import { downloadReportData } from "@/data/repository/ProjectRepository";

import UserPickerComponent from "@/components/common/UserPickerComponent.vue";
import MultiSelectionDialogComponent from "@/components/common/MultiSelectionDialog.vue";
import DatePickerCompoment from "@/components/common/DatePicker.vue";
import TimePickerComponent from "@/components/common/TimePicker.vue";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import { filterOutUndefined, formatNumber } from "@/utils/Utils";
import { ceilFromOneDecimal } from "@/utils/NumberRounding";
import { parseLocalizedFloat } from "@/utils/LocalisationHelper";
import { ButtonState } from "@/components/common/StateButton.vue";
import { filterUsersForSelection, findUser, getReactiveActiveUser } from "@/data/repository/UserRepository";
import { ProjectDetails } from "@/data/models/ProjectDetails";
import { isSimulatingErp } from "@/data/repository/ClientRepository";
import User from "@/data/models/User";
import { UserCapability } from "@/data/models/UserCapability";
import { doesUserHaveCapability } from "@/data/models/RoleSettings";

interface BaseStepDialogArgsSelectionOption {
  formatter: (toFormat: number) => string;
  options: number[];
  selectedOption: number;
  title: string;
  requiresSelection?: boolean;
}

interface BaseStepDialogArgsDatePickerOption {
  selectedDate?: firestore.Timestamp;
  title: string;
}

interface BaseStepDialogArgsAppointmentOption {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentAppointment: any;
}

interface BaseStepDialogArgsTextInputOption {
  title: string;
  initialText?: string;
  type: string;
  /** Grid width of the field; defaults to 12 (full width) when omitted. */
  cols?: number;
  /** Force read-only regardless of the dialog-wide edit state. */
  readonly?: boolean;
}

/**
 * An extra numeric field rendered in the same row as the primary text input.
 * Each value is returned in the save result under `numberInputs[key]`, already
 * parsed (`null` when the field is empty or unreadable).
 *
 * Whole numbers only: a key that types anything but a digit is dropped, and a
 * pasted decimal is rounded to one decimal, then up (`ceilFromOneDecimal`), when the
 * field loses focus; a negative value collapses to 0. A paste is still read in the
 * current locale's format, because a German user's `42,5` is not `425`.
 */
interface BaseStepDialogArgsNumberInput {
  key: string;
  title: string;
  /** Initial value; a whole number, since the field takes whole numbers only. */
  initialValue?: number | null;
  /** Grid width of the field; defaults to 4 when omitted. */
  cols?: number;
  /** Force read-only regardless of the dialog-wide edit state. */
  readonly?: boolean;
}

interface BaseStepReportData {
  internalReportRef?: string | null;
  externalReportRef?: string | null;
  csvRef?: string | null;
  jsonRef?: string | null;
}

interface BaseStepDialogArgs {
  title: string;

  editEnabled: boolean;
  creationDate?: Date;
  createdBy?: string;

  appointmentCreatedBy?: string;
  appointmentCreationDate?: Date;

  projectDetails: ProjectDetails;
  stepIndex: number;
  textInputOption?: BaseStepDialogArgsTextInputOption;
  numberInputs?: BaseStepDialogArgsNumberInput[];

  selectionOption?: BaseStepDialogArgsSelectionOption;
  contactPersons?: BaseStepDialogArgsSelectionOption;

  onlyShowCreationDetail?: boolean;
  appointmentOption?: BaseStepDialogArgsAppointmentOption;
  datePickerOption?: BaseStepDialogArgsDatePickerOption;
  hideSaveButton?: boolean;
  reportData?: BaseStepReportData;

  isContactPersonDependent?: boolean;
}

@Component({
  components: {
    picker: PickerDialogComponent,
    "base-dialog": BaseDialogComponent,
    "user-picker": UserPickerComponent,
    "date-picker": DatePickerCompoment,
    "time-picker": TimePickerComponent,
    "multi-selection-dialog": MultiSelectionDialogComponent,
  },
})
export default class BaseStepDialogComponent extends Vue {
  onlyShowCreationDetail = false;

  creationDate = firestore.Timestamp.fromDate(new Date());
  createdBy = UserModule.activeUserIdentifier;

  appointmentCreatedBy = UserModule.activeUserIdentifier;
  appointmentCreationDate = firestore.Timestamp.fromDate(new Date());;

  appointmentOption: BaseStepDialogArgsAppointmentOption | null = null;
  appointmentTimestamp: firestore.Timestamp | null = null;
  appointmentInitialUserId: string | null = null;
  /** User ids of the additional (team) users assisting the main responsible user. */
  appointmentAdditionalUserIds: string[] = [];
  appointmentNote: string | null = null;

  textInputOption: BaseStepDialogArgsTextInputOption | null = null;
  textInput = "";

  /** Extra numeric fields shown in the same row as the primary text input. */
  numberInputs: BaseStepDialogArgsNumberInput[] = [];
  /** Current string values of the extra numeric fields, keyed by `key`. */
  numberInputValues: Record<string, string> = {};

  projectDetails!: ProjectDetails;
  saveDisabled = false;
  isDeleting = false;
  showingErrorSnackbar = false;
  errorSnackbarText = "";
  showingSuccessSnackbar = false;
  successSnackbarText = "";
  stepIndex = 0;

  isContactPersonDependent = false;

  selectionOption: BaseStepDialogArgsSelectionOption | null = null;
  contactPersons: BaseStepDialogArgsSelectionOption | null = null;

  selectedOption: number | null = null;
  contactPerson: number | null = null;

  datePickerOption: BaseStepDialogArgsDatePickerOption | null = null;

  reportData: BaseStepReportData | null = null;
  selectedDate: firestore.Timestamp | null = null;

  editEnabled = true;

  usersForProjetOwnerSelection: User[] = [];

  skipAddingAppointmentInfo = false;
  skipAddingBeginInfo = false;

  onAppointmentTimestampChanged(newValue: firestore.Timestamp) {
    if (newValue) {
      this.appointmentTimePicker.setNewInternalDate(null);
      this.$nextTick(() => {
        this.appointmentTimePicker.textField.focus();
      });
    }
  }

  getUsersToSelectFrom(projectDetails: ProjectDetails): User[] {
    const users = filterUsersForSelection(UserModule.users);

    const branch = projectDetails.clientData.branch;

    if (!branch) {
      return users;
    }

    return users.filter(
      (user) =>
        !user.branches ||
        user.branches.length === 0 ||
        user.branches.includes(branch)
    );
  }

  get activeUser() {
    return getReactiveActiveUser();
  }

  get createdByUserName() {
    if (!this.createdBy) {
      return this.activeUser?.nickname;
    }

    return findUser(isSimulatingErp() && this.createdBy.includes("-") ? this.createdBy.split("-")[1] : this.createdBy)?.nickname ?? this.createdBy;
  }

  get appointmentCreatedByUserName() {
    if (!this.appointmentCreatedBy) {
      return this.activeUser?.nickname;
    }

    return findUser(isSimulatingErp() && this.appointmentCreatedBy.includes("-") ? this.appointmentCreatedBy.split("-")[1] : this.appointmentCreatedBy)?.nickname ?? this.appointmentCreatedBy;
  }

  @Ref("userPicker")
  userPicker!: UserPickerComponent;

  @Ref("additionalUsersDialog")
  additionalUsersDialog!: MultiSelectionDialogComponent<User>;

  @Ref("appointmentDatePicker")
  appointmentDatePicker!: DatePickerCompoment;

  @Ref("appointmentTimePicker")
  appointmentTimePicker!: TimePickerComponent;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  get showReportData() {
    return this.reportData != null;
  }

  get showDivider() {
    return (
      this.appointmentOption ||
      this.textInputOption ||
      this.selectionOption ||
      this.numberInputs.length > 0
    );
  }

  /**
   * Width of the primary text input. Shrinks to the option's configured `cols`
   * (default 4) when extra numeric inputs share the row; otherwise full width.
   */
  get primaryTextInputCols() {
    if (this.numberInputs.length > 0) {
      return this.textInputOption?.cols ?? 4;
    }
    return 12;
  }

  /**
   * Parsed values of every extra numeric field, keyed as the caller declared them.
   *
   * @returns each field's value, `null` where it is empty or unreadable
   */
  collectNumberInputs(): { [key: string]: number | null } {
    const values: { [key: string]: number | null } = {};
    this.numberInputs.forEach((numberInput) => {
      values[numberInput.key] = this.parseNumberInput(
        this.numberInputValues[numberInput.key]
      );
    });

    return values;
  }

  /**
   * Reads an extra numeric field, in the current locale's number format.
   *
   * @param raw what the user typed
   * @returns the value as a whole number (`ceilFromOneDecimal`) floored at 0, or
   *   `null` when the field is empty or does not read as a number
   */
  parseNumberInput(raw?: string): number | null {
    // Returns NaN for an empty or unreadable field.
    const parsed = parseLocalizedFloat(raw);
    if (!Number.isFinite(parsed)) {
      return null;
    }

    return Math.max(0, ceilFromOneDecimal(parsed));
  }

  /**
   * Keep an extra numeric field to digits: a key that would type anything else (a
   * decimal separator, a sign, a letter) is dropped. Editing keys and shortcuts such
   * as Ctrl+V pass through; `normalizeNumberInput` catches a pasted decimal on blur.
   *
   * @param event the field's keydown event
   */
  blockNonDigitKey(event: KeyboardEvent) {
    const typesCharacter = event.key.length === 1 && !event.ctrlKey && !event.metaKey;
    if (typesCharacter && !/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * The text an extra numeric field shows for a value: **no** thousands separator,
   * which keeps a typed value easy to correct and avoids re-reading a separator the
   * user never typed (and could not type, since the field drops it).
   *
   * @param value value to show, or `null` for an empty field
   * @returns the text to put in the field
   */
  formatNumberInput(value: number | null): string {
    return value == null ? "" : formatNumber(value, false);
  }

  /**
   * Show an extra numeric field the way it will be saved: as a whole number
   * (`ceilFromOneDecimal`), floored at 0. An empty field stays
   * empty, and text that does not read as a number is cleared rather than silently
   * saved as nothing.
   *
   * @param key `key` of the numeric field to normalise
   */
  normalizeNumberInput(key: string) {
    const raw = this.numberInputValues[key];
    if (raw == null || raw.trim() === "") {
      Vue.set(this.numberInputValues, key, "");
      return;
    }

    Vue.set(
      this.numberInputValues,
      key,
      this.formatNumberInput(this.parseNumberInput(raw))
    );
  }

  get dateString() {
    const creationDate = this.appointmentOption && this.appointmentCreationDate
      ? this.appointmentCreationDate
      : this.creationDate;

    return formatDateShort(creationDate);
  }

  get timeOfDateString() {
    const creationDate = this.appointmentOption && this.appointmentCreationDate
      ? this.appointmentCreationDate
      : this.creationDate;

    return formatTime(creationDate);
  }

  get showSelectNothingOption() {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve?: (value: any) => void = undefined;
  reject?: (reason: string) => void = undefined;

  onSave() {
    if (
      this.selectionOption &&
      this.selectionOption.requiresSelection &&
      this.selectedOption == null
    ) {
      return;
    }

    this.baseDialogComponent.isShowing = false;

    const selectedUser = this.userPicker?.selectedUser;

    const userIdentifier: string | null = this.resolveUserIdentifier(selectedUser);

    const createdBy = this.createdBy && this.createdBy !== "" 
      ? this.createdBy 
      : this.createdByUserName;
    const creationTimestamp = this.appointmentOption && !this.skipAddingBeginInfo
      ? this.creationDate.toDate().toISOString()
      : new Date().toISOString();

    const appointmentCreatedBy = this.appointmentCreatedBy && this.appointmentCreatedBy !== "" 
      ? this.appointmentCreatedBy 
      : this.appointmentCreatedByUserName;
    const appointmentCreationTimestamp = new Date().toISOString();

    this.resolve &&
      this.resolve(
        filterOutUndefined({
          appointmentCreatedBy: this.appointmentOption && !this.skipAddingAppointmentInfo ? appointmentCreatedBy : undefined,
          appointmentCreationTimestamp: this.appointmentOption && !this.skipAddingAppointmentInfo ? appointmentCreationTimestamp : undefined,

          createdBy: this.appointmentOption ? "" : createdBy,
          creationTimestamp: this.appointmentOption ? "" : creationTimestamp,

          responsibleUser: this.appointmentOption ? userIdentifier : undefined,
          additionalUsers: this.appointmentOption
            ? this.collectAdditionalUserIdentifiers()
            : undefined,
          completed: this.appointmentOption
            ? /* this.appointmentOption.currentAppointment?.completed ?? */ false
            : undefined,

          note: this.appointmentOption ? this.appointmentNote : undefined,

          contactPersonNumber: this.isContactPersonDependent
            ? this.contactPerson ?? 0
            : undefined,

          appointmentDate: this.appointmentOption
            ? this.collectAppointmentDate()?.toISOString()
            : undefined,

          selectedOption:
            this.selectionOption && !this.appointmentOption
              ? this.selectedOption
              : undefined,

          textInput: this.textInputOption ? this.textInput : undefined,

          numberInputs: this.numberInputs.length
            ? this.collectNumberInputs()
            : undefined,

          selectedDate: this.datePickerOption
            ? this.selectedDate?.toDate()?.toISOString()
            : undefined,
        })
      );
  }

  /** Resolve a user to the stored identifier form (staffNumber → nickname), like responsibleUser. */
  resolveUserIdentifier(user: User | null | undefined): string | null {
    if (!user) {
      return null;
    }

    return user.staffNumber && user.staffNumber !== ""
      ? user.staffNumber
      : user.nickname;
  }

  /** Selected additional-user ids resolved to stored identifiers (empty/unknown dropped). */
  collectAdditionalUserIdentifiers(): string[] {
    return this.appointmentAdditionalUserIds
      .filter((id) => id && id !== "")
      .map((id) => this.resolveUserIdentifier(findUser(id)))
      .filter((identifier): identifier is string => !!identifier && identifier !== "");
  }

  /** Selectable additional users: the appointment's user pool minus the main user. */
  get additionalUserOptions(): User[] {
    return this.usersForProjetOwnerSelection.filter(
      (user) => user.id !== this.appointmentInitialUserId
    );
  }

  /** Comma-separated nicknames of the currently selected additional users. */
  get additionalUsersDisplay(): string {
    return this.additionalUserOptions
      .filter((user) => this.appointmentAdditionalUserIds.includes(user.id))
      .map((user) => user.nickname)
      .join(", ");
  }

  /** Open the multi-select dialog; result replaces the additional-user selection. */
  async openAdditionalUsersDialog() {
    if (!this.editEnabled) {
      return;
    }

    const options = this.additionalUserOptions;
    const current = options.filter((user) =>
      this.appointmentAdditionalUserIds.includes(user.id)
    );

    const selected = await this.additionalUsersDialog.show({
      options,
      current,
      label: this.$t("common.additionalUsers").toString(),
      formatter: (user: User) => user.nickname,
      showSelectNothingOption: false,
    });

    this.appointmentAdditionalUserIds = selected.map((user) => user.id);
  }

  async show(baseDialogStepArgs: BaseStepDialogArgs) {
    this.saveDisabled = false;
    this.isDeleting = false;
    this.showingErrorSnackbar = false;
    this.errorSnackbarText = "";
    this.showingSuccessSnackbar = false;
    this.successSnackbarText = "";
    this.projectDetails = baseDialogStepArgs.projectDetails;
    this.stepIndex = baseDialogStepArgs.stepIndex;

    this.textInputOption = baseDialogStepArgs.textInputOption ?? null;
    this.textInput = baseDialogStepArgs.textInputOption?.initialText ?? "";

    this.numberInputs = baseDialogStepArgs.numberInputs ?? [];
    this.numberInputValues = {};
    this.numberInputs.forEach((numberInput) => {
      // Vue.set so the per-key entries are reactive in Vue 2.
      Vue.set(
        this.numberInputValues,
        numberInput.key,
        this.formatNumberInput(numberInput.initialValue ?? null)
      );
    });

    this.selectionOption = baseDialogStepArgs.selectionOption ?? null;
    this.selectedOption = this.selectionOption?.selectedOption ?? null;

    this.contactPersons = baseDialogStepArgs.contactPersons ?? null;
    this.contactPerson = this.contactPersons?.selectedOption ?? null;

    this.isContactPersonDependent =
      baseDialogStepArgs.isContactPersonDependent ?? false;

    this.appointmentOption = baseDialogStepArgs.appointmentOption ?? null;
    this.appointmentInitialUserId = null;
    this.appointmentAdditionalUserIds = [];

    this.editEnabled = baseDialogStepArgs.editEnabled;

    const currentAppointment = this.appointmentOption?.currentAppointment;

    this.usersForProjetOwnerSelection = this.getUsersToSelectFrom(
      this.projectDetails
    ).filter((user) => {
      const capabilityMap: { [key: number]: UserCapability[] } = {
        4: [UserCapability.LEAK_DETECTION],
        6: [UserCapability.DAMAGE_ASSESSMENT_LARGE],
        10: [UserCapability.DRYING],
        12: [UserCapability.DRYING],
        14: [UserCapability.DRYING],
        16: [UserCapability.REPAIR]
      };

      const stepIndex = this.stepIndex;
      const additionalCapabilities = capabilityMap[stepIndex] || [];

      return additionalCapabilities.every(cap => doesUserHaveCapability(user, cap));
    });

    if (baseDialogStepArgs.creationDate) {
      this.creationDate = firestore.Timestamp.fromDate(
        baseDialogStepArgs.creationDate
      );
    } else {
      this.creationDate = firestore.Timestamp.fromDate(new Date());
    }

    if (baseDialogStepArgs.appointmentCreationDate) {
      this.appointmentCreationDate = firestore.Timestamp.fromDate(
        baseDialogStepArgs.appointmentCreationDate
      );
    } else {
      this.appointmentCreationDate = firestore.Timestamp.fromDate(new Date());
    }

    if (baseDialogStepArgs.createdBy) {
      const isSimulatingErp_ = isSimulatingErp();
      const userId = isSimulatingErp_
        ? findUser(baseDialogStepArgs.createdBy.includes("-") 
            ? baseDialogStepArgs.createdBy.split("-")[1]
            : baseDialogStepArgs.createdBy
          )?.id
        : findUser(baseDialogStepArgs.createdBy)?.id

      const isValidSimulatingErp = baseDialogStepArgs.createdBy.includes("-");

      this.createdBy =
        userId 
          ? isSimulatingErp_ && isValidSimulatingErp
            ? "ERP/"+userId
            : userId
          : isSimulatingErp_ && isValidSimulatingErp
            ? "ERP/"+baseDialogStepArgs.createdBy
            : baseDialogStepArgs.createdBy;
    } else if (baseDialogStepArgs.createdBy === "") {
      this.skipAddingBeginInfo = true;
    } else {
      this.createdBy = UserModule.activeUserIdentifier;
    }

    if (baseDialogStepArgs.appointmentCreatedBy) {
      const isSimulatingErp_ = isSimulatingErp();
      const userId = isSimulatingErp_
        ? findUser(baseDialogStepArgs.appointmentCreatedBy.includes("-") 
            ? baseDialogStepArgs.appointmentCreatedBy.split("-")[1]
            : baseDialogStepArgs.appointmentCreatedBy
          )?.id
        : findUser(baseDialogStepArgs.appointmentCreatedBy)?.id

      const isValidSimulatingErp = baseDialogStepArgs.appointmentCreatedBy.includes("-");

      this.appointmentCreatedBy =
        userId 
          ? isSimulatingErp_ && isValidSimulatingErp
            ? "ERP/"+userId
            : userId
          : isSimulatingErp_ && isValidSimulatingErp
            ? "ERP/"+baseDialogStepArgs.appointmentCreatedBy
            : baseDialogStepArgs.appointmentCreatedBy;
    } else if (baseDialogStepArgs.appointmentCreatedBy === "") {
      this.skipAddingAppointmentInfo = true;
    } else {
      this.appointmentCreatedBy = UserModule.activeUserIdentifier;
    }

    if (currentAppointment) {
      this.appointmentTimestamp = currentAppointment.appointmentDate
        ? firestore.Timestamp.fromDate(
            new Date(currentAppointment.appointmentDate)
          )
        : null;

      this.appointmentNote = currentAppointment.note ?? null;

      const responsibleUser = findUser(currentAppointment.responsibleUser);

      if (responsibleUser) {
        this.appointmentInitialUserId = responsibleUser.id;
      }

      // Resolve stored additional users (raw identifiers) to their internal ids for
      // the pickers; drop any that no longer resolve to a known user.
      this.appointmentAdditionalUserIds = (currentAppointment.additionalUsers ?? [])
        .map((identifier: string) => findUser(identifier)?.id)
        .filter((id: string | undefined): id is string => !!id);
    } else {
      this.appointmentTimestamp = null;
      this.appointmentNote = null;
    }

    this.onlyShowCreationDetail =
      baseDialogStepArgs.onlyShowCreationDetail ?? false;

    this.datePickerOption = baseDialogStepArgs.datePickerOption ?? null;
    this.selectedDate =
      baseDialogStepArgs.datePickerOption?.selectedDate ?? null;

    this.reportData =
      baseDialogStepArgs.reportData === undefined
        ? null
        : baseDialogStepArgs.reportData;

    this.baseDialogComponent.show({
      title: baseDialogStepArgs.title,
      positiveButtonText: !baseDialogStepArgs.editEnabled
        ? null
        : this.$t("common.save").toString(),
      neutralButtonText:
        currentAppointment && baseDialogStepArgs.editEnabled
          ? this.$t("common.delete").toString()
          : undefined,
      closeOnSave: false,
      closeOnNeutralAction: false,
      hideSaveButton: !this.editEnabled
    });

    this.checkSaveEnabled();

    return new Promise<string>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  async onDownloadReportData(ref?: string | null) {
    if (!ref) {
      return;
    }

    await downloadReportData(ref);
  }

  collectAppointmentDate() {
    const dateStamp = this.appointmentDatePicker.internal;
    const hoursAndMinutes = this.appointmentTimePicker.hoursAndMinutes;

    let date = dateStamp?.toDate();

    if (!date) {
      if (hoursAndMinutes) {
        date = new Date();
      } else {
        return null;
      }
    }

    date.setHours(hoursAndMinutes.hours, hoursAndMinutes.minutes, 0);

    return date;
  }

  @Watch("selectedOption")
  checkSaveEnabled() {
    this.saveDisabled =
      this.selectionOption?.requiresSelection === true &&
      this.selectedOption === null;
  }

  showReportButton(ref?: string | null) {
    return ref !== undefined;
  }

  getButtonState(ref?: string | null) {
    return ref ? ButtonState.RED : ButtonState.DISABLED;
  }

  get setInitalTime() {
    return !!this.appointmentOption?.currentAppointment;
  }

  showErrorSnackbar(message: string) {
    this.errorSnackbarText = message;
    this.showingErrorSnackbar = true;
  }

  async onDelete() {
    if (this.isDeleting) return;

    const currentAppointment = this.appointmentOption?.currentAppointment;

    if (currentAppointment) {
      const project = this.projectDetails;
      this.isDeleting = true;

      try {
        const result = await deleteAppointment(
          project,
          this.stepIndex,
          currentAppointment
        );

        if (result.alreadyCompleted) {
          this.showErrorSnackbar(this.$t("projectStatus.appointmentAlreadyCompleted").toString());
          return;
        }

        if (result.status !== 200) {
          this.showErrorSnackbar(this.$t("projectStatus.appointmentDeleteError").toString());
          return;
        }

        if (project.syncTarget) {
          await updateSyncTargets(project.id, project.syncTarget, true, false);
        }

        this.baseDialogComponent.isShowing = false;
        this.successSnackbarText = this.$t("projectStatus.appointmentDeleteSuccess").toString();
        this.showingSuccessSnackbar = true;
      } catch (e) {
        this.showErrorSnackbar(this.$t("projectStatus.appointmentDeleteError").toString());
      } finally {
        this.isDeleting = false;
      }
    }
  }
}
